import i18next from 'i18next';
import moment from 'moment';

/**
 * The platform serves several locales under a code that is neither a BCP-47 language tag
 * nor a moment locale name. Neither library rejects them: `Intl` falls back to en-US, and
 * moment silently activates a different language that owns the tag - `se` is Northern Sami,
 * not Swedish, and `ml` is Malayalam, not Malay. Both tables are required for correctness.
 */
const INTL_LOCALE_OVERRIDES: { [code: string]: string } = {
    gr: 'el',
    ua: 'uk',
    se: 'sv',
    ml: 'ms',
    no: 'nb',
    zh: 'zh-CN',
    'pt-br': 'pt-BR',
};

/** Platform code -> moment locale name and its module. `en` is built into moment. */
const MOMENT_LOCALES: { [code: string]: { name: string; load: () => Promise<unknown> } } = {
    ar: { name: 'ar', load: () => import('moment/locale/ar') },
    bg: { name: 'bg', load: () => import('moment/locale/bg') },
    cs: { name: 'cs', load: () => import('moment/locale/cs') },
    de: { name: 'de', load: () => import('moment/locale/de') },
    es: { name: 'es', load: () => import('moment/locale/es') },
    fa: { name: 'fa', load: () => import('moment/locale/fa') },
    fr: { name: 'fr', load: () => import('moment/locale/fr') },
    gr: { name: 'el', load: () => import('moment/locale/el') },
    he: { name: 'he', load: () => import('moment/locale/he') },
    hr: { name: 'hr', load: () => import('moment/locale/hr') },
    hu: { name: 'hu', load: () => import('moment/locale/hu') },
    id: { name: 'id', load: () => import('moment/locale/id') },
    it: { name: 'it', load: () => import('moment/locale/it') },
    ja: { name: 'ja', load: () => import('moment/locale/ja') },
    ko: { name: 'ko', load: () => import('moment/locale/ko') },
    ml: { name: 'ms', load: () => import('moment/locale/ms') },
    nl: { name: 'nl', load: () => import('moment/locale/nl') },
    no: { name: 'nb', load: () => import('moment/locale/nb') },
    pl: { name: 'pl', load: () => import('moment/locale/pl') },
    pt: { name: 'pt', load: () => import('moment/locale/pt') },
    'pt-br': { name: 'pt-br', load: () => import('moment/locale/pt-br') },
    ro: { name: 'ro', load: () => import('moment/locale/ro') },
    ru: { name: 'ru', load: () => import('moment/locale/ru') },
    se: { name: 'sv', load: () => import('moment/locale/sv') },
    sk: { name: 'sk', load: () => import('moment/locale/sk') },
    sl: { name: 'sl', load: () => import('moment/locale/sl') },
    th: { name: 'th', load: () => import('moment/locale/th') },
    tr: { name: 'tr', load: () => import('moment/locale/tr') },
    ua: { name: 'uk', load: () => import('moment/locale/uk') },
    uz: { name: 'uz', load: () => import('moment/locale/uz') },
    vi: { name: 'vi', load: () => import('moment/locale/vi') },
    zh: { name: 'zh-cn', load: () => import('moment/locale/zh-cn') },
};

const normalize = (code?: string): string => (code || 'en').toLowerCase();

/** BCP-47 tag for `Intl.*`, for the language i18next is currently on. */
export const getIntlLocale = (): string => {
    const language = normalize(i18next.language);
    const candidate = INTL_LOCALE_OVERRIDES[language] || i18next.language || 'en';
    // i18next.language is seeded from an unsanitised `?lang=` query parameter, so a malformed
    // tag such as `en_US` reaches this far. Intl throws a RangeError on those, and numberPipe's
    // catch would swallow it and render every amount unformatted for the rest of the session.
    try {
        // Constructing the same formatter numberPipe uses is the precondition test itself.
        new Intl.NumberFormat(candidate);
        return candidate;
    } catch (error) {
        return 'en';
    }
};

/**
 * Load the moment locale for `code` and activate it. Safe to call repeatedly - moment keeps
 * locales it already holds. Must be awaited before dates render in the target language.
 */
export const applyMomentLocale = async (code?: string): Promise<void> => {
    const language = normalize(code);
    if (language === 'en') {
        moment.locale('en');
        return;
    }
    const locale = MOMENT_LOCALES[language];
    if (!locale) {
        // Loud on purpose: a language enabled in the backend config but missing here renders
        // English dates, which is the exact failure this table exists to prevent.
        console.warn(`[i18n] no moment locale mapped for "${code}" - dates will render in English`);
        moment.locale('en');
        return;
    }
    try {
        await locale.load();
        moment.locale(locale.name);
        // `moment.locale()` does not throw for a locale the instance never registered - it keeps
        // the previous one and returns it. That is how French dates stayed English while the HTTP
        // request for the locale chunk returned 200 and this try/catch never fired: the chunk had
        // registered the locale on a second copy of moment. Assert against our own instance.
        if (moment.locale() !== locale.name) {
            console.error(
                `[i18n] moment accepted "${locale.name}" but is still on "${moment.locale()}". ` +
                    'The locale registered on a different moment instance - check that the bundler ' +
                    'resolves a single copy (vite.config resolve.alias).'
            );
            moment.locale('en');
        }
    } catch (error) {
        console.warn(`[i18n] failed to load moment locale "${locale.name}" for "${code}"`, error);
        moment.locale('en');
    }
};
